import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import StatusBadge, { PlatformTag } from "@/components/StatusBadge";
import {
  ArrowLeft,
  Sparkles,
  RefreshCw,
  Save,
  Wand2,
  BookOpen,
  Send,
  Magnet,
  Mail,
  Target,
  CheckCircle2,
  CalendarClock,
  Rocket,
  Upload,
  FileText,
  Download,
  Copy,
  Share2,
  ExternalLink,
  DollarSign,
  Package,
  TrendingUp,
  Search,
  Hammer,
  Crown,
  PencilLine,
  Film,
  Video,
  Loader2,
  Volume2,
  ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { CreditFooter } from "@/components/CreditFooter";
import { CreditCost } from "@/components/CreditCost";
import { AffiliateDisclosure } from "@/components/AffiliateDisclosure";
import { SystemeExport } from "@/components/SystemeExport";

// Reel Hub is temporarily hidden from all users — to be rebuilt and re-enabled later.
// Set to true to restore the tab. The tab content below is intentionally kept, not deleted.
const REEL_HUB_ENABLED = false;

export default function CampaignDetail() {
  const { cid } = useParams();
  const navigate = useNavigate();
  const { refreshCredits } = useAuth();
  const [campaign, setCampaign] = useState(null);
  const [course, setCourse] = useState(null);
  const [bundle, setBundle] = useState(null);
  const [genBundle, setGenBundle] = useState(false);
  const [strategy, setStrategy] = useState(null);
  const [genStrategy, setGenStrategy] = useState(false);
  const [worksheetAnswers, setWorksheetAnswers] = useState({});
  const [savingAnswers, setSavingAnswers] = useState(false);
  const lastSavedAnswers = useRef("{}");
  const [reel, setReel] = useState(null);
  const [genReel, setGenReel] = useState(false);
  const [aspect, setAspect] = useState("9:16");
  const [genVoice, setGenVoice] = useState(false);
  const [retryIdx, setRetryIdx] = useState(null);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [content, setContent] = useState([]);
  const [genCourse, setGenCourse] = useState(false);
  const [genCampaign, setGenCampaign] = useState(false);
  const [saving, setSaving] = useState(false);
  const [regenIdx, setRegenIdx] = useState(null);
  const [sources, setSources] = useState([]);
  const [uploadingSrc, setUploadingSrc] = useState(false);
  const [integ, setInteg] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [recipient, setRecipient] = useState("");
  const [sending, setSending] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [sharing, setSharing] = useState(false);
  const [genCover, setGenCover] = useState(false);
  const [caps, setCaps] = useState({ is_admin: false, google_key_set: false, elevenlabs_key_set: false });
  const [brand, setBrand] = useState(null);

  const generateCover = async () => {
    setGenCover(true);
    try {
      const { data } = await api.post(`/campaigns/${cid}/generate-cover`);
      setCampaign((c) => ({ ...c, cover_url: data.cover_url }));
      toast.success("Cover image generated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cover generation failed");
    } finally {
      setGenCover(false);
    }
  };

  const shareDeck = async () => {
    setSharing(true);
    try {
      const { data } = await api.post(`/campaigns/${cid}/share`);
      // Share the crawler-friendly preview URL (server-rendered OG tags; redirects humans to the deck).
      const url = `${API}/public/decks/${data.public_id}/preview`;
      setShareUrl(url);
      setShareOpen(true);
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Public link created & copied!");
      } catch {
        toast.success("Public link created!");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create link");
    } finally {
      setSharing(false);
    }
  };

  const load = async () => {
    const { data } = await api.get(`/campaigns/${cid}`);
    setCampaign(data);
    setCourse(data.course);
    setBundle(data.bundle || null);
    setStrategy(data.strategy || null);
    setWorksheetAnswers(data.worksheet_answers || {});
    lastSavedAnswers.current = JSON.stringify(data.worksheet_answers || {});
    setReel(data.reel || null);
    setAspect(data.reel?.aspect_ratio || "9:16");
    const [src, ig] = await Promise.all([
      api.get(`/campaigns/${cid}/sources`).catch(() => ({ data: [] })),
      api.get(`/integrations/status`).catch(() => ({ data: null })),
    ]);
    setSources(src.data);
    setInteg(ig.data);
    api.get(`/settings`).then((r) => setCaps(r.data)).catch(() => {});
    api.get(`/brand`).then((r) => setBrand(r.data)).catch(() => {});
    if (data.campaign_status === "generated") {
      const c = await api.get(`/campaigns/${cid}/content`);
      setContent(c.data);
    }
  };

  const uploadSource = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingSrc(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/campaigns/${cid}/sources`, fd);
      setSources((s) => [...s, data]);
      toast.success(`Added ${data.original_filename}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingSrc(false);
      e.target.value = "";
    }
  };

  const exportPdf = async () => {
    try {
      const res = await api.get(`/campaigns/${cid}/export-pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(course?.title || "course").replace(/[^a-z0-9]+/gi, "-").toLowerCase() || "course"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Could not download the PDF. Please try again.");
    }
  };

  const sendEmail = async () => {
    setSending(true);
    try {
      await api.post(`/campaigns/${cid}/send-email`, { recipient_email: recipient });
      toast.success(`Email sent to ${recipient}`);
      setEmailOpen(false);
      setRecipient("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send email");
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    load();
    // Reload whenever the campaign id changes; load is defined per-render and stable in intent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cid]);

  const generateCourse = async () => {
    setGenCourse(true);
    try {
      await api.post(`/campaigns/${cid}/generate-course`);
      refreshCredits();
      // Course generation runs in the background (supports up to 16 lessons) — poll for completion.
      for (let i = 0; i < 75; i++) {
        await new Promise((r) => setTimeout(r, 4000));
        const { data } = await api.get(`/campaigns/${cid}`);
        if (data.course_status === "generated") {
          setCourse(data.course);
          setCampaign((c) => ({ ...c, course_status: "generated" }));
          toast.success("Mini course generated!");
          // Auto Cover: offer to generate a matching cover if none exists yet and user can generate.
          if ((caps.is_admin || caps.google_key_set) && !data.cover_url) {
            toast("Add an AI cover to finish the look?", {
              description: "Generate a matching cover image for this campaign.",
              action: { label: "Generate cover", onClick: () => generateCover() },
              duration: 12000,
            });
          }
          return;
        }
        if (data.course_status === "failed") {
          toast.error("Generation failed. Please try again.");
          refreshCredits();
          return;
        }
      }
      toast.error("Still generating — refresh in a moment to see your course.");
    } catch (e) {
      if (e.response?.status !== 402) toast.error(e.response?.data?.detail || "Generation failed");
    } finally {
      setGenCourse(false);
    }
  };

  const saveCourse = async () => {
    setSaving(true);
    try {
      await api.put(`/campaigns/${cid}/course`, course);
      toast.success("Saved");
    } finally {
      setSaving(false);
    }
  };

  const saveAllContent = async () => {
    setSaving(true);
    try {
      await Promise.all((content || []).map((it) => api.put(`/content/${it.id}`, { hook: it.hook, body: it.body })));
      toast.success("Saved");
    } catch (e) {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const regenLesson = async (idx) => {
    setRegenIdx(idx);
    try {
      const { data } = await api.post(`/campaigns/${cid}/regenerate-lesson`, { index: idx });
      const lessons = [...course.lessons];
      lessons[idx] = data;
      setCourse({ ...course, lessons });
      toast.success(`Lesson ${idx + 1} regenerated`);
    } catch (e) {
      toast.error("Could not regenerate");
    } finally {
      setRegenIdx(null);
    }
  };

  const updateLesson = (idx, key, value) => {
    const lessons = [...course.lessons];
    lessons[idx] = { ...lessons[idx], [key]: value };
    setCourse({ ...course, lessons });
  };

  const saveAnswers = async (force = true) => {
    const snapshot = JSON.stringify(worksheetAnswers);
    if (!force && snapshot === lastSavedAnswers.current) return;
    setSavingAnswers(true);
    try {
      await api.put(`/campaigns/${cid}/worksheet-answers`, { answers: worksheetAnswers });
      lastSavedAnswers.current = snapshot;
      toast.success("Answers saved — the AI will now personalize new content to your business");
    } catch (e) {
      toast.error("Could not save answers");
    } finally {
      setSavingAnswers(false);
    }
  };

  const generateCampaign = async () => {
    setGenCampaign(true);
    try {
      const { data } = await api.post(`/campaigns/${cid}/generate-campaign`);
      setContent(data.items);
      setCampaign((c) => ({ ...c, campaign_status: "generated", extras: data.extras }));
      toast.success("Social campaign generated!");
      refreshCredits();
    } catch (e) {
      if (e.response?.status !== 402) toast.error(e.response?.data?.detail || "Generation failed");
    } finally {
      setGenCampaign(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      if (status === "published") {
        await api.post(`/content/${id}/publish`);
      } else {
        await api.put(`/content/${id}/status`, { status });
      }
      setContent((items) => items.map((it) => (it.id === id ? { ...it, status } : it)));
      toast.success(`Marked ${status}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const editItem = async (id, field, value) => {
    setContent((items) => items.map((it) => (it.id === id ? { ...it, [field]: value } : it)));
  };
  const saveItem = async (item) => {
    await api.put(`/content/${item.id}`, { hook: item.hook, body: item.body });
    toast.success("Saved");
  };

  const copyToClipboard = async (text, msg = "Copied!") => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    toast.success(msg);
  };

  // Format one lesson as a clean slide: Title / Subtitle / Bullets
  const formatSlide = (l, i) => {
    const bullets = [];
    if (l.script) bullets.push(l.script.trim());
    if (l.exercise) bullets.push(`Exercise: ${l.exercise.trim()}`);
    if (l.worksheet) bullets.push(`Worksheet: ${l.worksheet.trim()}`);
    if (l.reflection) bullets.push(`Reflection: ${l.reflection.trim()}`);
    const title = l.title || `Lesson ${i + 1}`;
    const subtitle = `Lesson ${i + 1} · ${course?.title || "Mini Course"}`;
    return `${title}\n${subtitle}\n\n${bullets.map((b) => `• ${b}`).join("\n")}`;
  };

  // Format one section (Title / Subtitle=section / Bullets=lines)
  const formatSection = (lessonTitle, sectionLabel, value) => {
    const lines = (value || "").split("\n").map((s) => s.trim()).filter(Boolean);
    return `${lessonTitle || "Lesson"}\n${sectionLabel}\n\n${lines.map((b) => `• ${b}`).join("\n")}`;
  };

  const copyAllSlides = () => {
    const all = (course?.lessons || [])
      .map((l, i) => formatSlide(l, i))
      .join("\n\n———\n\n");
    copyToClipboard(all, "Copied all slides!");
  };

  const generateBundle = async () => {
    setGenBundle(true);
    try {
      const { data } = await api.post(`/campaigns/${cid}/generate-bundle`);
      setBundle(data);
      toast.success("Pricing bundle generated!");
      refreshCredits();
    } catch (err) {
      if (err.response?.status !== 402) toast.error(err.response?.data?.detail || "Generation failed");
    } finally {
      setGenBundle(false);
    }
  };

  const generateStrategy = async () => {
    setGenStrategy(true);
    try {
      const { data } = await api.post(`/campaigns/${cid}/generate-strategy`);
      setStrategy(data);
      toast.success("Sales strategy generated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generation failed");
    } finally {
      setGenStrategy(false);
    }
  };

  const copyStrategy = () => {
    if (!strategy?.tiers) return;
    const lines = [`Find → Build → Launch Sales Strategy — ${course?.title || campaign?.name || ""}`, ""];
    strategy.tiers.forEach((t) => {
      lines.push(`■ ${t.name} (${t.price}) — ${t.label}`);
      if (t.description) lines.push(t.description);
      lines.push(`• Find: ${t.find || ""}`);
      lines.push(`• Build: ${t.build || ""}`);
      lines.push(`• Launch: ${t.launch || ""}`);
      if (t.modules?.length) lines.push(`Modules: ${t.modules.join(", ")}`);
      lines.push("");
    });
    copyToClipboard(lines.join("\n"), "Strategy copied!");
  };

  const generateReel = async () => {
    setGenReel(true);
    try {
      const { data } = await api.post(`/campaigns/${cid}/generate-reel`, { aspect_ratio: aspect });
      setReel(data);
      toast.success("Reel assets ready — video clips are generating");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generation failed");
    } finally {
      setGenReel(false);
    }
  };

  const retryClip = async (i) => {
    setRetryIdx(i);
    try {
      await api.post(`/campaigns/${cid}/retry-clip`, { index: i });
      setReel((r) => {
        if (!r) return r;
        const scenes = [...(r.scenes || [])];
        scenes[i] = { ...scenes[i], status: "queued", clip_url: null, error: null };
        return { ...r, scenes, status: "generating" };
      });
      toast.success(`Regenerating scene ${i + 1}…`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not retry clip");
    } finally {
      setRetryIdx(null);
    }
  };

  const generateVoiceover = async () => {
    setGenVoice(true);
    try {
      const { data } = await api.post(`/campaigns/${cid}/generate-voiceover`);
      setReel((r) => ({ ...r, audio_url: data.audio_url }));
      toast.success("AI voiceover generated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Voiceover failed");
    } finally {
      setGenVoice(false);
    }
  };

  const downloadZip = async () => {
    setDownloadingZip(true);
    try {
      const token = localStorage.getItem("h2i_token");
      const url = `${API}/campaigns/${cid}/reel-zip?auth=${token}`;
      window.open(url, "_blank");
    } finally {
      setTimeout(() => setDownloadingZip(false), 1200);
    }
  };

  useEffect(() => {
    if (reel?.status !== "generating") return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/campaigns/${cid}/reel`);
        setReel(data);
        if (!data || data.status !== "generating") clearInterval(t);
      } catch (e) {
        console.warn("Reel status poll failed:", e);
      }
    }, 6000);
    return () => clearInterval(t);
  }, [reel?.status, cid]);

  const fileUrl = (path) => `${API.replace(/\/api$/, "")}${path}?auth=${localStorage.getItem("h2i_token")}`;

  if (!campaign) {
    return (
      <div className="flex justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  const extras = campaign.extras;
  const bPrimary = brand?.primary_color || "#1a2a6c";
  const bAccent = brand?.accent_color || "#00b09b";
  const PREMIUM_MSG = "Premium feature: Add your own API key in Settings to enable.";
  const videoGate = caps.is_admin || caps.google_key_set;
  const imageGate = caps.is_admin || caps.google_key_set;
  const voiceGate = caps.is_admin || caps.elevenlabs_key_set;

  return (
    <div className="space-y-8" data-testid="campaign-detail">
      <div>
        <button onClick={() => navigate("/dashboard")} className="mb-4 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="back-dashboard">
          <ArrowLeft size={16} /> Back to dashboard
        </button>
        <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0">
            <h1 className="font-head text-4xl font-bold">{campaign.name}</h1>
            <p className="mt-2 max-w-2xl text-muted-foreground">{campaign.topic}</p>
            {campaign.created_at && (
              <p className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground" data-testid="campaign-created-at">
                <CalendarClock size={14} />
                Created {new Date(campaign.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
              </p>
            )}
            <div className="mt-4 flex flex-wrap gap-2">
              {(campaign.platforms || []).map((p) => <PlatformTag key={p} platform={p} />)}
              <span className="rounded-md bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground">
                {campaign.num_lessons} lessons · {campaign.calendar_days}-day calendar · {campaign.tone}
              </span>
            </div>
            {campaign.audience && (
              <p className="mt-3 text-sm text-muted-foreground" data-testid="campaign-audience">
                <span className="font-medium text-foreground">For:</span> {campaign.audience}
              </p>
            )}
          </div>

          {/* Project cover (Imagen 4, BYOK Google key) */}
          <div className="shrink-0" data-testid="cover-section">
            <div className="group relative h-40 w-40 overflow-hidden rounded-2xl border border-border bg-secondary">
              {campaign.cover_url ? (
                <img src={fileUrl(campaign.cover_url)} alt="Project cover" data-testid="cover-image"
                  className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full flex-col items-center justify-center gap-2 p-3 text-center text-muted-foreground">
                  <ImageIcon size={26} />
                  <p className="text-[11px] leading-tight">AI cover art (Imagen 4)</p>
                </div>
              )}
              {genCover && (
                <div className="absolute inset-0 flex items-center justify-center bg-background/70 backdrop-blur-sm">
                  <Loader2 size={26} className="animate-spin text-primary" />
                </div>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={generateCover} disabled={genCover || !imageGate}
              data-testid="generate-cover-btn" title={!imageGate ? PREMIUM_MSG : ""}
              className="mt-2 w-40 rounded-full">
              {genCover ? (
                <><RefreshCw size={14} className="mr-1.5 animate-spin" /> Generating…</>
              ) : campaign.cover_url ? (
                <><RefreshCw size={14} className="mr-1.5" /> Regenerate</>
              ) : (
                <><Sparkles size={14} className="mr-1.5" /> Generate cover</>
              )}
            </Button>
            {!imageGate && (
              <button type="button" onClick={() => navigate("/settings")} data-testid="cover-premium-hint"
                className="mt-1.5 block w-40 text-left text-[11px] leading-tight text-muted-foreground hover:text-primary">
                Premium — add your Google key in <span className="underline">Settings</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <Tabs defaultValue="course">
        <TabsList className="bg-secondary">
          <TabsTrigger value="course" data-testid="tab-course"><BookOpen size={16} className="mr-1.5" /> Mini Course</TabsTrigger>
          <TabsTrigger value="social" data-testid="tab-social"><Send size={16} className="mr-1.5" /> Social Content</TabsTrigger>
          <TabsTrigger value="strategy" data-testid="tab-strategy"><TrendingUp size={16} className="mr-1.5" /> Sales Strategy</TabsTrigger>
          {REEL_HUB_ENABLED && (
            <TabsTrigger value="reel" data-testid="tab-reel"><Film size={16} className="mr-1.5" /> Reel Hub</TabsTrigger>
          )}
          <TabsTrigger value="extras" data-testid="tab-extras"><Magnet size={16} className="mr-1.5" /> Lead Magnet & Email</TabsTrigger>
        </TabsList>

        {/* COURSE */}
        <TabsContent value="course" className="mt-6 space-y-6">
          {course && (
            <CourseActionBar onSave={saveCourse} saving={saving} onPdf={exportPdf} onShare={shareDeck} sharing={sharing}
              canvaType="presentation" canvaText={`${course?.title || ""}\n\n${course?.promise || ""}`} />
          )}
          <Card className="border-border p-5" data-testid="sources-card">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <FileText size={17} className="text-primary" />
                <span className="text-sm font-semibold">Source material</span>
                <span className="text-xs text-muted-foreground">
                  {sources.length > 0 ? `${sources.length} file(s) added` : "Optional — influences generation"}
                </span>
              </div>
              <label className="cursor-pointer">
                <input type="file" accept=".txt,.md,.csv,.pdf,.docx" className="hidden" onChange={uploadSource} data-testid="source-upload-input" />
                <span className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-medium transition-colors hover:bg-secondary">
                  <Upload size={14} /> {uploadingSrc ? "Uploading..." : "Upload source"}
                </span>
              </label>
            </div>
            {sources.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {sources.map((s) => (
                  <span key={s.id} className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-xs">
                    <FileText size={12} /> {s.original_filename}
                  </span>
                ))}
              </div>
            )}
          </Card>

          {!course ? (
            <EmptyGen
              icon={BookOpen}
              title="Generate your mini course"
              desc={`AI will build a ${campaign.num_lessons}-lesson beginner course with scripts, exercises, worksheets and sales copy.`}
              loading={genCourse}
              onClick={generateCourse}
              testid="generate-course-btn"
              label="Generate course"
              cost={15}
            />
          ) : (
            <div className="space-y-6">
              <Card className="border-border p-8">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-[0.2em]" style={{ color: bAccent }}>Course</p>
                    <Input value={course.title || ""} onChange={(e) => setCourse({ ...course, title: e.target.value })}
                      data-testid="course-title" style={{ color: bPrimary }} className="mt-2 border-0 bg-transparent px-0 font-head text-2xl font-bold focus-visible:ring-0" />
                    <Textarea value={course.promise || ""} onChange={(e) => setCourse({ ...course, promise: e.target.value })}
                      className="mt-2 border-0 bg-transparent px-0 text-muted-foreground focus-visible:ring-0" rows={2} />
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button variant="outline" onClick={generateCourse} disabled={genCourse} data-testid="regen-course-btn" className="rounded-full">
                      <RefreshCw size={16} className={`mr-1 ${genCourse ? "animate-spin" : ""}`} /> Regenerate
                    </Button>
                  </div>
                </div>
                <AffiliateDisclosure className="mt-2" />
                {course.outcomes?.length > 0 && (
                  <div className="mt-6 flex flex-wrap gap-2">
                    {course.outcomes.map((o, i) => (
                      <span key={o || i} style={{ backgroundColor: `${bAccent}26`, color: bAccent }} className="flex items-center gap-1.5 rounded-full px-3 py-1 text-sm">
                        <Target size={13} /> {o}
                      </span>
                    ))}
                  </div>
                )}
              </Card>

              <Accordion type="single" collapsible className="space-y-3">
                {(course.lessons || []).map((l, i) => (
                  <AccordionItem key={i} value={`l-${i}`} className="rounded-xl border border-border bg-card px-5">
                    <div className="flex items-center">
                      <AccordionTrigger className="flex-1 hover:no-underline" data-testid={`lesson-trigger-${i}`}>
                        <span className="flex items-center gap-3 text-left">
                          <span style={{ backgroundColor: `${bAccent}1a`, color: bAccent }} className="flex h-7 w-7 items-center justify-center rounded-full text-sm font-bold">{i + 1}</span>
                          <span className="font-head font-semibold" style={{ color: bPrimary }}>{l.title}</span>
                        </span>
                      </AccordionTrigger>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); copyToClipboard(formatSlide(l, i)); }}
                        data-testid={`copy-slide-${i}`}
                        title="Copy slide"
                        className="ml-2 inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                      >
                        <Copy size={13} /> Copy Slide
                      </button>
                    </div>
                    <AccordionContent className="space-y-4 pb-6">
                      <LessonField label="Title" value={l.title} onChange={(v) => updateLesson(i, "title", v)}
                        onCopy={() => copyToClipboard(formatSection(l.title, "Title", l.title), "Copied!")} testid={`copy-section-${i}-title`} />
                      <LessonField label="Teaching script" value={l.script} onChange={(v) => updateLesson(i, "script", v)} rows={6}
                        onCopy={() => copyToClipboard(formatSection(l.title, "Teaching script", l.script), "Copied!")} testid={`copy-section-${i}-script`} />
                      <LessonField label="Exercise" value={l.exercise} onChange={(v) => updateLesson(i, "exercise", v)} rows={3}
                        onCopy={() => copyToClipboard(formatSection(l.title, "Exercise", l.exercise), "Copied!")} testid={`copy-section-${i}-exercise`} />
                      <LessonField label="Worksheet" value={l.worksheet} onChange={(v) => updateLesson(i, "worksheet", v)} rows={3}
                        onCopy={() => copyToClipboard(formatSection(l.title, "Worksheet", l.worksheet), "Copied!")} testid={`copy-section-${i}-worksheet`} />
                      <div style={{ borderColor: `${bAccent}4d`, backgroundColor: `${bAccent}0d` }} className="rounded-xl border p-4">
                        <label className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide" style={{ color: bAccent }}>
                          <PencilLine size={13} /> Your worksheet answer
                        </label>
                        <p className="mt-0.5 text-xs text-muted-foreground">Fill this in — the AI reuses your answers to personalize future lessons, hooks & strategies.</p>
                        <Textarea value={worksheetAnswers[i] || ""} onChange={(e) => setWorksheetAnswers((a) => ({ ...a, [i]: e.target.value }))}
                          onBlur={() => saveAnswers(false)} rows={3} className="mt-2 bg-card" data-testid={`worksheet-answer-${i}`}
                          placeholder="Write your answer here..." />
                      </div>
                      <LessonField label="Reflection prompt" value={l.reflection} onChange={(v) => updateLesson(i, "reflection", v)} rows={2}
                        onCopy={() => copyToClipboard(formatSection(l.title, "Reflection prompt", l.reflection), "Copied!")} testid={`copy-section-${i}-reflection`} />
                      <Button variant="outline" size="sm" onClick={() => regenLesson(i)} disabled={regenIdx === i}
                        data-testid={`regen-lesson-${i}`} className="rounded-full">
                        <Wand2 size={14} className="mr-1" /> {regenIdx === i ? "Regenerating..." : "Regenerate lesson"}
                      </Button>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>

              <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
                <Button onClick={saveAnswers} disabled={savingAnswers} variant="outline" data-testid="save-answers-btn"
                  className="rounded-full px-6 py-6 text-base font-semibold">
                  <Save size={18} className="mr-2" /> {savingAnswers ? "Saving..." : "Save Worksheet Answers"}
                </Button>
                <Button onClick={copyAllSlides} data-testid="copy-all-slides-btn"
                  className="rounded-full bg-primary px-8 py-6 text-base font-semibold hover:bg-primary/90">
                  <Copy size={18} className="mr-2" /> Copy All Slides
                </Button>
              </div>

              {course.sales_copy && (
                <Card className="border-border bg-secondary/50 p-6">
                  <p className="text-xs font-medium uppercase tracking-[0.2em]" style={{ color: bAccent }}>Sales copy</p>
                  <Textarea value={course.sales_copy} onChange={(e) => setCourse({ ...course, sales_copy: e.target.value })}
                    data-testid="sales-copy" className="mt-2 border-0 bg-transparent px-0 focus-visible:ring-0" rows={4} />
                </Card>
              )}

              {course.monetization?.length > 0 && (
                <div data-testid="monetization-section">
                  <h3 className="mb-4 flex items-center gap-2 font-head text-2xl font-bold" style={{ color: bPrimary }}>
                    <DollarSign size={20} style={{ color: bAccent }} /> Monetization Strategy
                  </h3>
                  <div className="grid gap-4 md:grid-cols-3">
                    {course.monetization.map((m, i) => (
                      <Card key={m.model || i} className="flex flex-col border-border p-5 transition-transform duration-200 hover:-translate-y-1" data-testid={`monetization-${i}`}>
                        <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: bAccent }}>{m.model}</span>
                        <p className="mt-2 font-head text-xl font-bold" style={{ color: bPrimary }}>{m.price}</p>
                        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{m.strategy}</p>
                        <button onClick={() => copyToClipboard(`${m.model} — ${m.price}\n${m.strategy}`)}
                          data-testid={`copy-monetization-${i}`}
                          className="mt-4 inline-flex w-fit items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-primary">
                          <Copy size={12} /> Copy
                        </button>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              <div data-testid="bundle-section">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                  <h3 className="flex items-center gap-2 font-head text-2xl font-bold" style={{ color: bPrimary }}>
                    <Package size={20} style={{ color: bAccent }} /> Pricing Bundles
                  </h3>
                  <Button variant={bundle ? "outline" : "default"} onClick={generateBundle} disabled={genBundle}
                    data-testid="generate-bundle-btn"
                    className={`rounded-full ${bundle ? "" : "bg-primary hover:bg-primary/90"}`}>
                    {genBundle ? <><RefreshCw size={15} className="mr-1.5 animate-spin" /> Generating...</>
                      : bundle ? <><RefreshCw size={15} className="mr-1.5" /> Regenerate<CreditCost n={5} /></>
                      : <><Sparkles size={15} className="mr-1.5" /> Generate Good/Better/Best<CreditCost n={5} /></>}
                  </Button>
                </div>
                {!bundle ? (
                  <Card className="border-dashed p-8 text-center text-muted-foreground">
                    Generate a 3-tier Good ($7) / Better ($27) / Best ($97) bundle with AI-written value props and bonuses.
                  </Card>
                ) : (
                  <div className="grid gap-4 md:grid-cols-3 md:items-stretch">
                    {(bundle.tiers || []).map((t, i) => {
                      const best = t.name === "Best";
                      return (
                        <Card key={t.name || i} data-testid={`bundle-tier-${i}`}
                          className={`relative flex flex-col p-6 transition-transform duration-200 hover:-translate-y-1 ${best ? "border-primary ring-2 ring-primary" : "border-border"}`}>
                          {best && (
                            <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-0.5 text-xs font-semibold text-primary-foreground">
                              Best value
                            </span>
                          )}
                          <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">{t.name}</p>
                          <p className="mt-2 font-head text-4xl font-bold">{t.price}</p>
                          {t.tagline && <p className="mt-1 text-sm font-medium text-muted-foreground">{t.tagline}</p>}
                          {t.value_proposition && <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{t.value_proposition}</p>}
                          {t.includes?.length > 0 && (
                            <ul className="mt-4 space-y-1.5">
                              {t.includes.map((inc, k) => (
                                <li key={`${t.name}-inc-${inc}-${k}`} className="flex items-start gap-2 text-sm">
                                  <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-accent" /> {inc}
                                </li>
                              ))}
                            </ul>
                          )}
                          {t.bonuses?.length > 0 && (
                            <div className="mt-4 rounded-xl bg-secondary/60 p-3">
                              <p className="text-xs font-bold uppercase tracking-wide text-primary">Bonuses</p>
                              <ul className="mt-1.5 space-y-1">
                                {t.bonuses.map((b, k) => (
                                  <li key={`${t.name}-bonus-${b}-${k}`} className="flex items-start gap-2 text-sm text-muted-foreground">
                                    <Sparkles size={13} className="mt-0.5 shrink-0 text-primary" /> {b}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {t.high_value_bonus && (
                            <div className="mt-3 rounded-xl border border-primary/30 bg-primary/5 p-3">
                              <p className="text-xs font-bold uppercase tracking-wide text-primary">High-value bonus</p>
                              <p className="mt-1 text-sm text-foreground">{t.high_value_bonus}</p>
                            </div>
                          )}
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </TabsContent>

        {/* REEL HUB */}
        <TabsContent value="reel" className="mt-6">
          {!reel ? (
            <Card className="flex flex-col items-center justify-center gap-5 border-dashed border-border p-14 text-center" data-testid="reel-empty">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <Film size={30} />
              </div>
              <div>
                <h3 className="font-head text-2xl font-bold">Build your Reel Asset Hub</h3>
                <p className="mx-auto mt-2 max-w-md text-muted-foreground">
                  AI writes a timed voiceover script, caption & hashtags, and generates 3 short video clips (Veo 3.1) for your key scenes — ready to download into your editor.
                </p>
              </div>
              <div className="w-full max-w-xs">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Aspect ratio</p>
                <div className="grid grid-cols-3 gap-2" data-testid="aspect-ratio-picker">
                  {[
                    { v: "9:16", label: "9:16", sub: "Reels/TikTok" },
                    { v: "1:1", label: "1:1", sub: "Feed" },
                    { v: "16:9", label: "16:9", sub: "YouTube" },
                  ].map((o) => (
                    <button
                      key={o.v}
                      type="button"
                      onClick={() => setAspect(o.v)}
                      data-testid={`aspect-${o.v.replace(":", "-")}`}
                      className={`flex flex-col items-center rounded-xl border p-3 text-sm transition-colors ${
                        aspect === o.v ? "border-primary bg-primary/5 text-foreground" : "border-border text-muted-foreground hover:bg-secondary"
                      }`}
                    >
                      <span className="font-bold">{o.label}</span>
                      <span className="text-[11px]">{o.sub}</span>
                    </button>
                  ))}
                </div>
              </div>
              <Button onClick={generateReel} disabled={genReel || !videoGate} data-testid="generate-reel-btn"
                title={!videoGate ? PREMIUM_MSG : ""}
                className="rounded-full bg-primary px-8 py-6 text-base font-semibold hover:bg-primary/90">
                {genReel ? (
                  <><RefreshCw size={18} className="mr-2 animate-spin" /> Generating with AI...</>
                ) : (
                  <><Sparkles size={18} className="mr-2" /> Generate reel assets</>
                )}
              </Button>
              {!videoGate && (
                <button type="button" onClick={() => navigate("/settings")} data-testid="reel-premium-hint"
                  className="text-sm text-muted-foreground hover:text-primary">
                  {PREMIUM_MSG}
                </button>
              )}
            </Card>
          ) : (
            <div className="space-y-6" data-testid="reel-section">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <h3 className="font-head text-2xl font-bold">Reel Asset Hub</h3>
                  <span data-testid="reel-status" className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                    reel.status === "ready" ? "bg-accent/15 text-accent"
                    : reel.status === "failed" ? "bg-destructive/15 text-destructive"
                    : reel.status === "partial" ? "bg-yellow-500/15 text-yellow-700"
                    : "bg-secondary text-muted-foreground"}`}>
                    {reel.status === "generating" ? "Clips generating…" : reel.status}
                  </span>
                  {reel.aspect_ratio && (
                    <span data-testid="reel-aspect" className="rounded-full bg-secondary px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                      {reel.aspect_ratio}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="mr-1 flex items-center gap-1 rounded-full border border-border p-0.5" data-testid="reel-aspect-picker">
                    {["9:16", "1:1", "16:9"].map((v) => (
                      <button key={v} type="button" onClick={() => setAspect(v)} disabled={reel.status === "generating"}
                        data-testid={`reel-aspect-${v.replace(":", "-")}`}
                        className={`rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${
                          aspect === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"
                        }`}>
                        {v}
                      </button>
                    ))}
                  </div>
                  <Button variant="outline" onClick={downloadZip} disabled={downloadingZip || reel.status === "generating"}
                    data-testid="download-zip-btn" className="rounded-full">
                    <Package size={15} className="mr-1.5" /> {downloadingZip ? "Preparing…" : "Download All (ZIP)"}
                  </Button>
                  <Button variant="outline" onClick={generateReel} disabled={genReel || reel.status === "generating" || !videoGate}
                    data-testid="regen-reel-btn" title={!videoGate ? PREMIUM_MSG : ""} className="rounded-full">
                    <RefreshCw size={15} className={`mr-1 ${genReel ? "animate-spin" : ""}`} /> Regenerate
                  </Button>
                </div>
              </div>

              {/* Video clips */}
              <div>
                <h4 className="mb-3 flex items-center gap-2 font-head text-lg font-bold">
                  <Video size={18} className="text-primary" /> Video clips <span className="text-sm font-normal text-muted-foreground">(Veo 3.1)</span>
                </h4>
                <div className="grid gap-4 md:grid-cols-3">
                  {(reel.scenes || []).map((sc, i) => {
                    const ar = reel.aspect_ratio === "1:1" ? "aspect-square" : reel.aspect_ratio === "16:9" ? "aspect-video" : "aspect-[9/16]";
                    return (
                    <Card key={`${sc.title || "scene"}-${i}`} className="flex flex-col overflow-hidden border-border" data-testid={`reel-clip-${i}`}>
                      <div className={`flex ${ar} items-center justify-center bg-secondary`}>
                        {sc.status === "ready" && sc.clip_url ? (
                          <video src={fileUrl(sc.clip_url)} controls playsInline className="h-full w-full object-cover" data-testid={`reel-video-${i}`} />
                        ) : sc.status === "failed" ? (
                          <div className="p-4 text-center">
                            <p className="text-sm font-semibold text-destructive">Clip failed</p>
                            <p className="mt-1 text-xs text-muted-foreground">{sc.error}</p>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center gap-2 text-muted-foreground">
                            <Loader2 size={26} className="animate-spin text-primary" />
                            <p className="text-xs">Generating…</p>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center justify-between gap-2 p-3">
                        <p className="text-sm font-semibold">{sc.title}</p>
                        <div className="flex items-center gap-2">
                          {sc.status !== "queued" && (
                            <button type="button" onClick={() => retryClip(i)} disabled={retryIdx === i || reel.status === "generating" || !videoGate}
                              data-testid={`reel-retry-${i}`} title={!videoGate ? PREMIUM_MSG : "Regenerate this clip"}
                              className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-primary disabled:opacity-50">
                              <RefreshCw size={12} className={retryIdx === i ? "animate-spin" : ""} /> Retry
                            </button>
                          )}
                          {sc.status === "ready" && sc.clip_url && (
                            <a href={fileUrl(sc.clip_url)} download data-testid={`reel-download-${i}`}
                              className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-primary">
                              <Download size={12} /> Download
                            </a>
                          )}
                        </div>
                      </div>
                    </Card>
                    );
                  })}
                </div>
              </div>

              {/* Voiceover */}
              <Card className="border-border p-6" data-testid="reel-voiceover">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h4 className="font-head text-lg font-bold">Voiceover script</h4>
                  <div className="flex gap-2">
                  <div className="flex flex-col items-end gap-1">
                    <Button variant="outline" size="sm" onClick={generateVoiceover} disabled={genVoice || !voiceGate}
                      data-testid="generate-voiceover-btn" title={!voiceGate ? PREMIUM_MSG : ""} className="rounded-full">
                      <Volume2 size={14} className="mr-1" /> {genVoice ? "Generating…" : reel.audio_url ? "Regenerate voiceover" : "AI Voiceover"}
                    </Button>
                    {!voiceGate && (
                      <button type="button" onClick={() => navigate("/settings")} data-testid="voiceover-premium-hint"
                        className="text-[11px] text-muted-foreground hover:text-primary">
                        Premium — add your ElevenLabs key in Settings
                      </button>
                    )}
                  </div>
                    <Button variant="ghost" size="sm" onClick={() => copyToClipboard(reel.voiceover)} data-testid="copy-voiceover" className="rounded-full">
                      <Copy size={14} className="mr-1" /> Copy
                    </Button>
                  </div>
                </div>
                {reel.audio_url && (
                  <audio controls src={fileUrl(reel.audio_url)} data-testid="reel-audio-player" className="mt-3 w-full" />
                )}
                <pre className="mt-3 whitespace-pre-wrap font-sans text-sm leading-relaxed text-muted-foreground">{reel.voiceover}</pre>
              </Card>

              {/* Captions & hashtags */}
              <div className="grid gap-4 md:grid-cols-2">
                <Card className="border-border p-6" data-testid="reel-caption">
                  <div className="flex items-center justify-between">
                    <h4 className="font-head text-lg font-bold">Caption</h4>
                    <Button variant="ghost" size="sm" onClick={() => copyToClipboard(reel.captions)} data-testid="copy-caption" className="rounded-full">
                      <Copy size={14} className="mr-1" /> Copy
                    </Button>
                  </div>
                  <p className="mt-3 whitespace-pre-wrap text-sm text-muted-foreground">{reel.captions}</p>
                </Card>
                <Card className="border-border p-6" data-testid="reel-hashtags">
                  <div className="flex items-center justify-between">
                    <h4 className="font-head text-lg font-bold">Hashtags</h4>
                    <Button variant="ghost" size="sm" onClick={() => copyToClipboard((reel.hashtags || []).join(" "))} data-testid="copy-hashtags" className="rounded-full">
                      <Copy size={14} className="mr-1" /> Copy
                    </Button>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {(reel.hashtags || []).map((h, i) => (
                      <span key={`${h}-${i}`} className="rounded-full bg-secondary px-3 py-1 text-sm text-secondary-foreground">{h}</span>
                    ))}
                  </div>
                </Card>
              </div>
            </div>
          )}
        </TabsContent>

        {/* SALES STRATEGY */}
        <TabsContent value="strategy" className="mt-6">
          {!course ? (
            <Card className="border-dashed p-12 text-center text-muted-foreground">
              Generate the mini course first to build your sales strategy.
            </Card>
          ) : !strategy ? (
            <EmptyGen
              icon={TrendingUp}
              title="Build your Find → Build → Launch strategy"
              desc="AI maps your course into a 3-tier ($7 / $27 / $97) sales strategy across the Find, Build and Launch phases with specific bonuses and modules."
              loading={genStrategy}
              onClick={generateStrategy}
              testid="generate-strategy-btn"
              label="Generate strategy"
            />
          ) : (
            <div className="space-y-5" data-testid="strategy-section">
              <CourseActionBar onSave={saveCourse} saving={saving} onPdf={exportPdf} onShare={shareDeck} sharing={sharing}
                canvaType="doc" canvaText={(strategy?.tiers || []).map((t) => `${t.name || ""} — ${t.price || ""}`).join("\n")} />
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="font-head text-2xl font-bold">Find → Build → Launch</h3>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={generateStrategy} disabled={genStrategy} data-testid="regen-strategy-btn" className="rounded-full">
                    <RefreshCw size={15} className={`mr-1 ${genStrategy ? "animate-spin" : ""}`} /> Regenerate
                  </Button>
                  <Button onClick={copyStrategy} data-testid="copy-strategy-btn" className="rounded-full bg-primary hover:bg-primary/90">
                    <Copy size={15} className="mr-1.5" /> Copy Strategy
                  </Button>
                </div>
              </div>

              <StrategyTable tiers={strategy.tiers || []} />
            </div>
          )}
        </TabsContent>

        {/* SOCIAL */}
        <TabsContent value="social" className="mt-6">
          {!course ? (
            <Card className="border-dashed p-12 text-center text-muted-foreground">
              Generate the mini course first, then repurpose it into social content.
            </Card>
          ) : content.length === 0 ? (
            <EmptyGen
              icon={Send}
              title="Repurpose into a social campaign"
              desc={`Turn your course into a ${campaign.calendar_days}-day content calendar of reels, hooks, captions and posts.`}
              loading={genCampaign}
              onClick={generateCampaign}
              testid="generate-campaign-btn"
              label="Generate campaign"
              cost={15}
            />
          ) : (
            <div className="space-y-4">
              <CourseActionBar onSave={saveAllContent} saving={saving} onPdf={exportPdf} onShare={shareDeck} sharing={sharing}
                canvaType="social" canvaText={(content || []).map((c) => c.hook).filter(Boolean).join("\n\n")} />
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <p className="text-sm text-muted-foreground">{content.length} content items</p>
                  {integ && (
                    <span
                      data-testid="publish-mode"
                      className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        integ.ayrshare?.connected
                          ? "bg-accent/15 text-accent"
                          : "bg-secondary text-muted-foreground"
                      }`}
                    >
                      {integ.ayrshare?.connected ? "Live publishing via Ayrshare" : "Simulated publishing"}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={generateCampaign} disabled={genCampaign} data-testid="regen-campaign-btn" className="rounded-full">
                    <RefreshCw size={15} className={`mr-1 ${genCampaign ? "animate-spin" : ""}`} /> Regenerate
                  </Button>
                </div>
              </div>
              {content.map((item) => (
                <Card key={item.id} className="border-border p-6" data-testid={`content-item-${item.id}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-md bg-primary/10 px-2 py-0.5 text-xs font-bold text-primary">Day {item.day}</span>
                    <PlatformTag platform={item.platform} />
                    <span className="text-xs font-medium text-muted-foreground">{item.type}</span>
                    <div className="ml-auto"><StatusBadge status={item.status} /></div>
                  </div>
                  <div className="mt-4 space-y-3">
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Hook</label>
                      <Textarea value={item.hook} onChange={(e) => editItem(item.id, "hook", e.target.value)}
                        rows={2} className="mt-1 font-medium" data-testid={`hook-${item.id}`} />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Content</label>
                      <Textarea value={item.body} onChange={(e) => editItem(item.id, "body", e.target.value)}
                        rows={5} className="mt-1" data-testid={`body-${item.id}`} />
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => saveItem(item)} className="rounded-full">
                      <Save size={14} className="mr-1" /> Save edits
                    </Button>
                    <div className="ml-auto flex gap-2">
                      {item.status === "draft" && (
                        <Button size="sm" onClick={() => setStatus(item.id, "approved")} data-testid={`approve-${item.id}`}
                          className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90">
                          <CheckCircle2 size={14} className="mr-1" /> Approve
                        </Button>
                      )}
                      {item.status === "approved" && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => setStatus(item.id, "scheduled")} data-testid={`schedule-${item.id}`} className="rounded-full">
                            <CalendarClock size={14} className="mr-1" /> Schedule
                          </Button>
                          <Button size="sm" onClick={() => setStatus(item.id, "published")} data-testid={`publish-${item.id}`}
                            className="rounded-full bg-primary hover:bg-primary/90">
                            <Rocket size={14} className="mr-1" /> Publish
                          </Button>
                        </>
                      )}
                      {item.status === "scheduled" && (
                        <Button size="sm" onClick={() => setStatus(item.id, "published")} data-testid={`publish-${item.id}`}
                          className="rounded-full bg-primary hover:bg-primary/90">
                          <Rocket size={14} className="mr-1" /> Publish now
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* EXTRAS */}
        <TabsContent value="extras" className="mt-6 space-y-6">
          {extras && (
            <CourseActionBar onSave={saveCourse} saving={saving} onPdf={exportPdf} onShare={shareDeck} sharing={sharing}
              canvaType="email" canvaText={`${extras?.email?.subject || ""}\n\n${extras?.email?.body || ""}`} />
          )}
          {!extras ? (
            <Card className="border-dashed p-12 text-center text-muted-foreground">
              Generate the social campaign to get your lead magnet and promotional email.
            </Card>
          ) : (
            <div className="grid gap-6 lg:grid-cols-2">
              <Card className="border-border p-6" data-testid="lead-magnet">
                <div className="flex items-center gap-2 text-primary"><Magnet size={18} /><span className="font-semibold">Lead Magnet</span></div>
                <h3 className="mt-3 font-head text-xl font-bold">{extras.lead_magnet?.title}</h3>
                <p className="mt-3 whitespace-pre-wrap text-sm text-muted-foreground">{extras.lead_magnet?.content}</p>
              </Card>
              <Card className="border-border p-6" data-testid="promo-email">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-primary"><Mail size={18} /><span className="font-semibold">Promotional Email</span></div>
                  <div className="flex items-center gap-2">
                  <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="rounded-full bg-primary hover:bg-primary/90" data-testid="open-send-email-btn">
                        <Send size={14} className="mr-1" /> Send
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Send promotional email</DialogTitle>
                        <DialogDescription>Send the generated email to a recipient.</DialogDescription>
                      </DialogHeader>
                      {integ && !integ.resend?.connected ? (
                        <div className="rounded-lg border border-border bg-secondary/50 p-4 text-sm text-muted-foreground" data-testid="resend-not-connected">
                          Email sending isn't connected yet. Add a <span className="font-semibold text-foreground">RESEND_API_KEY</span> and verified sender to enable one-click sending. You can still copy the email text above.
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <label className="text-sm font-medium">Recipient email</label>
                            <Input type="email" value={recipient} onChange={(e) => setRecipient(e.target.value)}
                              placeholder="lead@example.com" data-testid="email-recipient" />
                          </div>
                          <DialogFooter>
                            <Button onClick={sendEmail} disabled={sending || !recipient} data-testid="send-email-confirm"
                              className="rounded-full bg-primary hover:bg-primary/90">
                              {sending ? "Sending..." : "Send email"}
                            </Button>
                          </DialogFooter>
                        </div>
                      )}
                    </DialogContent>
                  </Dialog>
                  </div>
                </div>
                <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Subject</p>
                <p className="font-head text-lg font-bold">{extras.email?.subject}</p>
                <p className="mt-3 whitespace-pre-wrap text-sm text-muted-foreground">{extras.email?.body}</p>
              </Card>
            </div>
          )}
          {extras && (
            <SystemeExport cid={cid} course={course} strategy={strategy} extras={extras}
              copy={copyToClipboard} systemeKeySet={caps.systeme_key_set} />
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle>Public slide deck link</DialogTitle>
            <DialogDescription>Share a read-only presentation of this course.</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Anyone with this link can view a clean, read-only presentation of this course. Re-share to refresh it with your latest edits.
          </p>
          <div className="flex items-center gap-2">
            <Input readOnly value={shareUrl} data-testid="share-url-input" className="flex-1" />
            <Button variant="outline" onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Link copied!"); }}
              data-testid="copy-share-link" className="rounded-full">
              <Copy size={15} />
            </Button>
          </div>
          <DialogFooter>
            <Button asChild className="rounded-full bg-primary hover:bg-primary/90">
              <a href={shareUrl} target="_blank" rel="noopener noreferrer" data-testid="open-deck-link">
                <ExternalLink size={15} className="mr-1.5" /> Open presentation
              </a>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <CreditFooter className="mt-8" />
    </div>
  );
}

function StrategyTable({ tiers }) {
  const phases = [
    { key: "find", label: "Find", icon: Search, sub: "Get clarity" },
    { key: "build", label: "Build", icon: Hammer, sub: "Create & implement" },
    { key: "launch", label: "Launch", icon: Rocket, sub: "Sell & grow" },
  ];
  const isWinner = (t) => t.name === "Best" || t.price === "$97";

  return (
    <div className="grid gap-4 lg:grid-cols-3 lg:items-start" data-testid="strategy-table">
      {tiers.map((t, i) => {
        const winner = isWinner(t);
        return (
          <Card
            key={t.name || i}
            data-testid={`strategy-tier-${i}`}
            className={`relative flex flex-col p-6 transition-transform duration-200 hover:-translate-y-1 ${
              winner ? "border-primary bg-primary/[0.03] shadow-lg ring-2 ring-primary lg:-mt-2 lg:mb-2" : "border-border"
            }`}
          >
            {winner && (
              <span className="absolute -top-3 left-1/2 flex -translate-x-1/2 items-center gap-1 whitespace-nowrap rounded-full bg-primary px-3 py-1 text-xs font-bold text-primary-foreground">
                <Crown size={13} /> Complete Transformation
              </span>
            )}
            <div className="flex items-baseline justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">{t.name}</p>
                <p className="text-sm font-medium text-muted-foreground">{t.label}</p>
              </div>
              <p className="font-head text-3xl font-bold">{t.price}</p>
            </div>
            {t.description && <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{t.description}</p>}

            <div className="mt-5 space-y-3">
              {phases.map((p) => {
                const Icon = p.icon;
                return (
                  <div key={p.key} className="rounded-xl border border-border bg-card p-3">
                    <div className="flex items-center gap-2">
                      <span className={`flex h-6 w-6 items-center justify-center rounded-md ${winner ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"}`}>
                        <Icon size={13} />
                      </span>
                      <span className="text-xs font-bold uppercase tracking-wide">{p.label}</span>
                      <span className="text-[11px] text-muted-foreground">· {p.sub}</span>
                    </div>
                    <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{t[p.key]}</p>
                  </div>
                );
              })}
            </div>

            {t.modules?.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-bold uppercase tracking-wide text-primary">Includes</p>
                <ul className="mt-1.5 space-y-1">
                  {t.modules.map((m, k) => (
                    <li key={m || k} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-accent" /> {m}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

function CourseActionBar({ onSave, saving, onPdf, onShare, sharing }) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-2" data-testid="course-action-bar">
      <Button onClick={onSave} disabled={saving} data-testid="action-save"
        className="h-10 rounded-full bg-primary hover:bg-primary/90">
        <Save size={16} className="mr-1.5" /> {saving ? "Saving..." : "Save"}
      </Button>
      <Button variant="outline" onClick={onPdf} data-testid="action-download-pdf" className="h-10 rounded-full">
        <Download size={16} className="mr-1.5" /> Download PDF
      </Button>
      <Button variant="outline" onClick={onShare} disabled={sharing} data-testid="action-share-deck" className="h-10 rounded-full">
        <Share2 size={16} className="mr-1.5" /> {sharing ? "Creating..." : "Share Deck"}
      </Button>
    </div>
  );
}

function EmptyGen({ icon: Icon, title, desc, loading, onClick, testid, label, cost }) {
  return (
    <Card className="flex flex-col items-center justify-center gap-5 border-dashed border-border p-14 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary">
        <Icon size={30} />
      </div>
      <div>
        <h3 className="font-head text-2xl font-bold">{title}</h3>
        <p className="mx-auto mt-2 max-w-md text-muted-foreground">{desc}</p>
      </div>
      <Button onClick={onClick} disabled={loading} data-testid={testid}
        className="rounded-full bg-primary px-8 py-6 text-base font-semibold hover:bg-primary/90">
        {loading ? (
          <><RefreshCw size={18} className="mr-2 animate-spin" /> Generating with AI...</>
        ) : (
          <><Sparkles size={18} className="mr-2" /> {label}{cost ? <CreditCost n={cost} /> : null}</>
        )}
      </Button>
    </Card>
  );
}

function LessonField({ label, value, onChange, rows = 1, onCopy, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</label>
        {onCopy && (
          <button type="button" onClick={onCopy} data-testid={testid} title="Copy section"
            className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-xs text-muted-foreground transition-colors hover:bg-secondary hover:text-primary">
            <Copy size={12} />
          </button>
        )}
      </div>
      <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows} className="mt-1" />
    </div>
  );
}
